import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'

import LabRuntime from '../shell/LabRuntime'
import { labApi } from '../api/labApi'
import type { Session } from '../types/session'

export interface LabSyncMessage {
  type: 'lab_sync'
  action: 'start' | 'end'
  data: {
    session_id: string
    game_slug?: string | null
    table_id: string
  }
  timestamp?: string
}

interface LabSessionViewerProps {
  tableId: string
  isMaster?: boolean
  userId?: string
  userFullName?: string
  lastMessage?: LabSyncMessage
}

interface ViewerState {
  isActive: boolean
  sessionId: string | null
}

export function LabSessionViewer({
  tableId,
  isMaster = false,
  userId,
  userFullName,
  lastMessage,
}: LabSessionViewerProps) {
  const [state, setState] = useState<ViewerState>({ isActive: false, sessionId: null })
  const [ending, setEnding] = useState(false)

  useEffect(() => {
    if (!lastMessage) return
    if (lastMessage.data?.table_id && lastMessage.data.table_id !== tableId) return

    if (lastMessage.action === 'start') {
      setState({ isActive: true, sessionId: lastMessage.data.session_id })
      toast.info('Aula iniciada pelo mestre')
    } else if (lastMessage.action === 'end') {
      setState({ isActive: false, sessionId: null })
      toast.info('Aula encerrada')
    }
  }, [lastMessage, tableId])

  const handleEnd = async () => {
    if (!state.sessionId) return
    setEnding(true)
    try {
      await labApi.endSession(state.sessionId)
      // O broadcast 'end' chega via WebSocket e dispara o useEffect acima
    } catch (err) {
      console.error('[LAB] erro ao encerrar aula:', err)
      toast.error('Erro ao encerrar a aula')
    } finally {
      setEnding(false)
    }
  }

  if (!state.isActive || !state.sessionId || !userId) return null

  const embedSession: Session = {
    sessionId: state.sessionId,
    userId,
    isAnonymous: false,
    role: isMaster ? 'master' : 'player',
    displayName: userFullName,
    tableId,
  }

  return (
    <div className="fixed inset-0 z-50 bg-game-primary overflow-auto">
      <LabRuntime embedSession={embedSession} />
      {isMaster && (
        <Button
          variant="destructive"
          size="sm"
          onClick={handleEnd}
          disabled={ending}
          style={{ position: 'fixed', top: 12, right: 12, zIndex: 70 }}
        >
          <X className="h-4 w-4 mr-1" />
          {ending ? 'Encerrando…' : 'Encerrar Aula'}
        </Button>
      )}
    </div>
  )
}
